import React, { useCallback, useEffect } from 'react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { concat, from, Subscription, switchMap } from 'rxjs'

import { Typography } from '@mui/material'
import { Preloader } from '@procom-labs/atoms'
import {
  ClientQueryParams,
  CommonMessages,
  RecaptchaActions,
  useRollbarInstance,
} from '@procom-labs/common'

import { Header } from '@auth-portal/components/header'
import { environment } from '@auth-portal/environment'
import { useAuthService } from '@auth-portal/hooks'

export const ActivateFboInvitation: React.FC<{}> = () => {
  const [searchParams] = useSearchParams()
  const { t } = useTranslation('main')
  const navigate = useNavigate()

  const authService = useAuthService()
  const rollbarInstance = useRollbarInstance()

  const { executeRecaptcha } = useGoogleReCaptcha()
  const emailParam = searchParams.get('email')
  const migrationToken = searchParams.get('migrationToken')
  const tenantParam = searchParams.get('tenant')
  const onboardingCodeParam = searchParams.get('onboardingCode')
  const destinationUrlParam = searchParams.get('destinationURL')

  const getRecaptchaToken = useCallback(async (): Promise<string> => {
    if (!executeRecaptcha) {
      throw new Error('executeRecaptcha is not ready')
    }
    try {
      return await executeRecaptcha(RecaptchaActions.ActivateFboMapping)
    } catch (e: any) {
      rollbarInstance.error(e)
      return ''
    }
  }, [executeRecaptcha])

  useEffect(() => {
    let subscription = new Subscription()
    if (
      emailParam &&
      tenantParam &&
      executeRecaptcha &&
      (migrationToken || onboardingCodeParam)
    ) {
      const email = decodeURIComponent(emailParam)

      subscription = from(getRecaptchaToken())
        .pipe(
          switchMap((token) =>
            concat(
              authService.activateFboUserMapping(
                migrationToken,
                onboardingCodeParam,
                email,
                token,
                tenantParam
              ),
              authService.verifyEmail({
                email,
                migrationToken,
                onboardingCode: onboardingCodeParam,
              })
            )
          )
        )
        .subscribe({
          complete: () => {
            if (destinationUrlParam) {
              localStorage.setItem(
                ClientQueryParams.destinationURL,
                destinationUrlParam
              )
            }
            navigate('/')
          },
          error: (err) => {
            rollbarInstance.error(err)
            navigate(`/?error=${CommonMessages.SomethingWrong}`)
          },
        })
    }

    return () => {
      if (subscription && !subscription.closed) {
        subscription.unsubscribe()
      }
    }
  }, [
    executeRecaptcha,
    getRecaptchaToken,
    navigate,
    emailParam,
    migrationToken,
  ])

  if (environment.IS_FBO_INVITATION_SIGNUP_MAINTENANCE_ENABLED === 'true') {
    return (
      <div>
        <Header heading={t('form.completeRegistration.maintenanceTitle')} />
        <Typography
          component="p"
          variant="body2"
          align="center"
          sx={{ whiteSpace: 'pre-wrap' }}
        >
          {t('form.completeRegistration.maintenanceContent')}
        </Typography>
      </div>
    )
  }

  return <Preloader center />
}
